import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import FractionInput from "../../../../components/inputs/FractionInput";
import { formatLatexPreview } from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function gcd(a, b) {
  return b === 0 ? Math.abs(a) : gcd(b, a % b);
}

function formatFraction(numerator, denominator) {
  const sign = Math.sign(numerator) * Math.sign(denominator);
  const absNum = Math.abs(numerator);
  const absDen = Math.abs(denominator);
  const d = gcd(absNum, absDen);
  const simpNum = absNum / d;
  const simpDen = absDen / d;

  if (simpNum % simpDen === 0) {
    return `${sign * (simpNum / simpDen)}`;
  }

  if (simpNum < simpDen) {
    return `${sign < 0 ? "-" : ""}\\dfrac{${simpNum}}{${simpDen}}`;
  }

  const whole = Math.floor(simpNum / simpDen);
  const remainder = simpNum % simpDen;
  return `${sign < 0 ? "-" : ""}${whole}\\dfrac{${remainder}}{${simpDen}}`;
}

function formatLinearLatex(a, b) {
  if (a === 0) return `f(x) = ${b}`;

  const aPart = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  if (b === 0) return `f(x) = ${aPart}`;

  return `f(x) = ${aPart} ${b > 0 ? "+" : "-"} ${Math.abs(b)}`;
}

function formatLinearLatexFromUser(aParsed, aRaw, bParsed, bRaw) {
  if (!Number.isFinite(aParsed) || !Number.isFinite(bParsed)) return null;

  const aLatexRaw = String(formatLatexPreview(aRaw || ""));
  const bLatexRaw = String(formatLatexPreview(bRaw || ""));
  const aLatex = aLatexRaw.includes("=") ? aLatexRaw.split("=")[1].trim() : aLatexRaw;
  const bLatex = bLatexRaw.includes("=") ? bLatexRaw.split("=")[1].trim() : bLatexRaw;

  if (aParsed === 0) {
    return `f(x) = ${bLatex}`;
  }

  const aPart = aParsed === 1 ? "x" : aParsed === -1 ? "-x" : `${aLatex}x`;
  if (bParsed === 0) {
    return `f(x) = ${aPart}`;
  }

  return `f(x) = ${aPart} ${bParsed > 0 ? "+" : "-"} ${bLatex.replace("-", "")}`;
}

function buildTaskData() {
  const xRange = [-6, 6];
  const yRange = [-6, 6];
  const readableMin = -5;
  const readableMax = 5;

  for (let attempt = 0; attempt < 500; attempt++) {
    const a = randInt(-3, 3);
    const b = randInt(-4, 4);
    const candidates = [];

    for (let x = readableMin; x <= readableMax; x++) {
      const y = a * x + b;
      if (Number.isInteger(y) && y >= readableMin && y <= readableMax) {
        candidates.push({ x, y });
      }
    }

    if (candidates.length < 2) continue;

    const point1 = candidates[0];
    const point2 = candidates[candidates.length - 1];

    if (point1.x === point2.x) continue;

    return {
      a,
      b,
      point1,
      point2,
      xRange,
      yRange,
    };
  }

  return {
    a: 1,
    b: 0,
    point1: { x: -2, y: -2 },
    point2: { x: 2, y: 2 },
    xRange,
    yRange,
  };
}

export function generateTask() {
  return {
    type: "LinearEquationFromGraphTask",
    data: buildTaskData(),
  };
}

export function renderTask({ data }) {
  const { a, b, xRange, yRange } = data;

  return (
    <div className="space-y-3">
      <p>
        Na wykresie przedstawiono funkcje liniowa <InlineMath math="f(x)=ax+b" />.
        Odczytaj dwa punkty nalezace do tego wykresu i na ich podstawie wyznacz
        wartosci wspolczynnika kierunkowego <InlineMath math="a" /> oraz wyrazu
        wolnego <InlineMath math="b" />.
      </p>

      <div style={{ maxWidth: 360 }}>
        <GraphViewer
          width={360}
          equations={[`y = ${a}*x + (${b})`]}
          points={{}}
          segments={[]}
          xRange={xRange}
          yRange={yRange}
        />
      </div>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer ?? {};

  return (
    <div className="flex gap-4 items-center flex-wrap">
      <div className="flex items-center gap-2">
        <span><InlineMath math="a = " /></span>
        <FractionInput
          value={ua.answerA || ""}
          onChange={(val) => onChange({ ...ua, answerA: val })}
          disabled={disabled}
          label="a"
        />
      </div>

      <div className="flex items-center gap-2">
        <span><InlineMath math="b = " /></span>
        <FractionInput
          value={ua.answerB || ""}
          onChange={(val) => onChange({ ...ua, answerB: val })}
          disabled={disabled}
          label="b"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;

  const aUser = userAnswer.answerA?.parsed;
  const bUser = userAnswer.answerB?.parsed;

  if (aUser == null || bUser == null) return false;

  return Math.abs(aUser - data.a) < 0.005 && Math.abs(bUser - data.b) < 0.005;
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer !== "object" ||
    !userAnswer.answerA?.raw ||
    !userAnswer.answerB?.raw
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const aValue = userAnswer.answerA?.parsed;
  const bValue = userAnswer.answerB?.parsed;
  const userFormulaLatex = formatLinearLatexFromUser(
    aValue,
    userAnswer.answerA.raw,
    bValue,
    userAnswer.answerB.raw
  );

  return (
    <div className="space-y-4 mt-4" style={{ textAlign: "left" }}>
      <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
        <InlineMath math={formatLatexPreview(userAnswer.answerA.raw, "a")} />
      </div>
      <div style={{ marginBottom: "1rem" }}>
        <InlineMath math={formatLatexPreview(userAnswer.answerB.raw, "b")} />
      </div>
      {userFormulaLatex ? (
        <>
          <strong>Wzor funkcji z Twojej odpowiedzi:</strong>
          <div style={{ display: "block", textAlign: "left", marginTop: "0.5rem" }}>
            <InlineMath math={`\\displaystyle ${userFormulaLatex}`} />
          </div>
        </>
      ) : null}
    </div>
  );
}

export function renderAnswer({ data }) {
  return (
    <div className="space-y-3 mt-4" style={{ textAlign: "left" }}>
      <div><InlineMath math={`a = ${data.a}`} /></div>
      <div><InlineMath math={`b = ${data.b}`} /></div>
      <div>
        <strong>Wzor funkcji:</strong>
      </div>
      <div>
        <InlineMath math={formatLinearLatex(data.a, data.b)} />
      </div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const { a, b, point1, point2, xRange, yRange } = data;
  const dx = point2.x - point1.x;
  const dy = point2.y - point1.y;
  const slopeLatex = formatFraction(dy, dx);
  const bLatex = formatFraction(b, 1);
  const ax1Latex = formatFraction(a * point1.x, 1);
  const fs = (v) => (v < 0 ? `(${v})` : `${v}`);

  const points = {
    A: { x: point1.x, y: point1.y, label: "A" },
    B: { x: point2.x, y: point2.y, label: "B" },
  };

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Z wykresu odczytujemy dwa punkty calkowite lezace na prostej:
        <BlockMath math={`A=(${point1.x},${point1.y}),\\quad B=(${point2.x},${point2.y})`} />
        <div style={{ margin: "0.75rem auto 0", maxWidth: 360 }}>
          <GraphViewer
            width={360}
            equations={[`y = ${a}*x + (${b})`]}
            points={points}
            segments={[]}
            xRange={xRange}
            yRange={yRange}
          />
        </div>
      </div>

      <div>
        <strong>Krok 2:</strong> Obliczamy wspolczynnik kierunkowy:
        <BlockMath math="a = \dfrac{y_2-y_1}{x_2-x_1}" />
        <BlockMath
          math={`a = \\dfrac{${fs(point2.y)}-${fs(point1.y)}}{${fs(point2.x)}-${fs(point1.x)}} = \\dfrac{${dy}}{${dx}} = ${slopeLatex}`}
        />
      </div>

      <div>
        <strong>Krok 3:</strong> Liczymy wyraz wolny <InlineMath math="b" /> ze wzoru <InlineMath math="b=y-ax" />:
        <BlockMath
          math={`b = ${fs(point1.y)} - ${fs(a)} \\cdot ${fs(point1.x)} = ${fs(point1.y)} - ${ax1Latex} = ${bLatex}`}
        />
      </div>

      <div>
        <strong>Krok 4:</strong> Zapisujemy wzor funkcji:
        <BlockMath math={formatLinearLatex(a, b)} />
      </div>
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
